import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { Bot, Check, MessageSquare, Globe, Palette, Zap, ArrowRight, Sparkles, Users } from "lucide-react";

const FEATURES = [
  { icon: Palette, title: "Fully branded", text: "Your colors, name and greeting — it feels like part of your site." },
  { icon: MessageSquare, title: "Answers from your info", text: "It only speaks from the details you give it — no made-up prices or promises." },
  { icon: Users, title: "Captures leads", text: "When it can't answer, it politely collects the visitor's name and email for you." },
  { icon: Globe, title: "Anywhere in minutes", text: "Share a link or paste one line of code to embed it on any website." },
];

const HIGHLIGHT = "growth";

export default function AiBotsPricing() {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/bot-plans/public").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
  }, []);

  return (
    <div className="bg-navy text-white" data-testid="ai-bots-pricing-page">
      <Helmet>
        <title>AI Chat Assistants for Your Business | ATF WebWorks</title>
        <meta name="description" content="A branded AI assistant that answers your customers 24/7 and captures leads while you sleep. Simple monthly plans from ATF WebWorks." />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{ background: "radial-gradient(600px 300px at 80% -10%, #C9A227, transparent)" }} />
        <div className="max-w-6xl mx-auto px-6 py-20 relative">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 text-gold px-3 py-1 text-xs uppercase tracking-widest mb-6">
              <Sparkles size={13} /> AI Assistant Add-on
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl text-white">
              An AI assistant that answers customers <span className="text-gold">24/7</span> — and never lets a lead slip.
            </h1>
            <p className="text-base md:text-lg text-gray-200 mt-6 max-w-2xl">
              We build you a branded chat assistant trained on your business. It answers questions instantly,
              and when it's unsure it collects the visitor's details so you can follow up. Add it to any website in minutes.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/book" data-testid="hero-book-btn" className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
                Book a quick call <ArrowRight size={17} />
              </Link>
              <a href="#plans" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-semibold hover:bg-white/5 transition-colors">
                See plans
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-300" data-testid="works-anywhere">
              <span className="inline-flex items-center gap-2"><Globe size={16} className="text-gold" /> Works on any website</span>
              <span className="text-gray-500 hidden sm:inline">·</span>
              <span className="inline-flex items-center gap-2"><Check size={16} className="text-gold" /> WordPress, Wix, Squarespace, Shopify or custom</span>
              <span className="text-gray-500 hidden sm:inline">·</span>
              <span className="inline-flex items-center gap-2"><Check size={16} className="text-gold" /> No website? Share a link</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="border border-white/10 bg-navy-800 p-6" data-testid={`feature-${i}`}>
              <span className="w-11 h-11 rounded-full bg-gold/10 text-gold flex items-center justify-center mb-4"><f.icon size={20} /></span>
              <div className="font-semibold text-white mb-1.5">{f.title}</div>
              <p className="text-sm text-gray-400 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Simple monthly plans</h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">Pick the size that fits your traffic. No setup fee — we build and brand it for you.</p>
        </div>

        {plans === null ? (
          <div className="text-center text-gray-500 py-10">Loading plans…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {plans.map((p, i) => {
              const hot = p.id === HIGHLIGHT || (plans.length === 3 && i === 1);
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  className={`relative flex flex-col p-8 border ${hot ? "border-gold bg-navy-800" : "border-white/10 bg-navy-800/60"}`}
                  data-testid={`plan-card-${p.id}`}>
                  {hot && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-[11px] font-bold uppercase tracking-wider px-3 py-1">Most popular</div>}
                  <div className="flex items-center gap-2 text-gold mb-3"><Bot size={18} /><span className="uppercase tracking-widest text-xs">{p.name}</span></div>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-4xl font-bold">${p.price_usd}</span>
                    <span className="text-gray-400 mb-1">/mo</span>
                  </div>
                  <div className="text-sm text-gray-400 mb-6">Up to <span className="text-white font-semibold">{p.monthly_messages.toLocaleString()}</span> messages / month</div>
                  <ul className="space-y-3 text-sm text-gray-300 flex-1">
                    <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" /> Branded to your business</li>
                    <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" /> Trained on your info & FAQs</li>
                    <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" /> Lead capture with email alerts</li>
                    <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" /> Hosted link + one-line embed</li>
                    <li className="flex gap-2"><Check size={16} className="text-gold shrink-0 mt-0.5" /> Setup & branding included</li>
                  </ul>
                  <Link to="/book" data-testid={`plan-cta-${p.id}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold transition-colors ${hot ? "bg-gold text-navy hover:bg-gold-light" : "border border-white/20 text-white hover:bg-white/5"}`}>
                    Get started <ArrowRight size={16} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-14 border-t border-white/10 pt-10">
          <div className="inline-flex items-center gap-2 text-gold mb-3"><Zap size={18} /> <span className="font-semibold">Not sure which plan?</span></div>
          <p className="text-gray-400 max-w-xl mx-auto mb-6">Tell us about your business and we'll recommend the right fit — and have your assistant live within days.</p>
          <Link to="/book" className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
            Book a free consultation <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </div>
  );
}
